import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import SpinnerButton from '@/components/common/SpinnerButton';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import HorizontalLogo from '@/components/common/HorizontalLogo';
import Page from '@/components/layout/Page';
import Input from '@/components/form/Input';
import { usePostForgotPassword } from '@/data/user';

const formSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' })
});

const ForgotPasswordPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const postForgotPassword = usePostForgotPassword();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ''
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setError('');
      await postForgotPassword(values);
      setSuccess(true);
      setSubmitting(false);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <div className="p-8">
        <HorizontalLogo />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Forgot Password</CardTitle>
          <CardDescription>
            Enter your email and we&apos;ll send you instructions to reset your
            password
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              {!success && (
                <Input
                  name="email"
                  label="Email"
                  placeholder="enter your email..."
                />
              )}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle size={18} />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert>
                  <AlertTitle>Success</AlertTitle>
                  <AlertDescription>
                    Check your email for password reset instructions
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex justify-end">
              {!success && (
                <SpinnerButton type="submit" loading={submitting}>
                  Send Reset Instructions
                </SpinnerButton>
              )}
            </CardFooter>
          </form>
        </Form>
      </Card>
    </Page>
  );
};

export default ForgotPasswordPage;
